"use client";
import * as React from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@AdbhutByte/utils/components/Link";
import SendIcon from "@mui/icons-material/Send";
import Grid from "@mui/material/Grid";
import NextImage from "next/image";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import dynamic from "next/dynamic";

const Player = dynamic(() => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player), { ssr: false, });
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import Paper from "@mui/material/Paper";
import LinkIcon from "@mui/icons-material/Link";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Collapse from "@mui/material/Collapse";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import * as yup from "yup";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  open as requestCVDialogueOpen,
  close as requestCVDialogueClose,
} from "@AdbhutByte/utils/config/ui/state/slices/requestCVDialogueSlice";
import { close as socialDialogueClose } from "@AdbhutByte/utils/config/ui/state/slices/socialDialogueSlice";
import {
  useAppDispatch,
  useAppSelector,
} from "@AdbhutByte/utils/functions/stateSelector";
import Stack from "@mui/material/Stack";

type CollapseMoreProps = {
  vital: boolean;
  edu: boolean;
  skill: boolean;
  learning: boolean;
};

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  reason: yup.string().required("Reason is required"),
});

type ExpandMoreOverlayProps = {
  overlay?: boolean;
};

const Image = styled(NextImage)({});
const MuiPlayer = styled(Player)({});
const ExpandMoreOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== "overlay",
  name: "ExpandMoreOverlay",
  slot: "Root",
})<ExpandMoreOverlayProps>(
  ({ overlay }) => `
  text-align: center;
  ${overlay
      ? `
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: end;
      height: 100px;
      width: 100%;
      bottom: 0;
      left: 0;
      background: rgb(0, 0, 51);
      background: -moz-linear-gradient(
        180deg,
        rgba(0, 0, 51, 0) 0%,
        rgba(0, 0, 51, 0.5) 31%,
        rgba(0, 0, 51, 0.7203081061526173) 46%,
        rgba(0, 0, 51, 0.8967786943879115) 66%,
        rgba(0, 0, 51, 1) 100%
      );
      background: -webkit-linear-gradient(
        180deg,
        rgba(0, 0, 51, 0) 0%,
        rgba(0, 0, 51, 0.5) 31%,
        rgba(0, 0, 51, 0.7203081061526173) 46%,
        rgba(0, 0, 51, 0.8967786943879115) 66%,
        rgba(0, 0, 51, 1) 100%
      );
      background: linear-gradient(
        180deg,
        rgba(0, 0, 51, 0) 0%,
        rgba(0, 0, 51, 0.5) 31%,
        rgba(0, 0, 51, 0.7203081061526173) 46%,
        rgba(0, 0, 51, 0.8967786943879115) 66%,
        rgba(0, 0, 51, 1) 100%
      );
      filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#000033",endColorstr="#000033",GradientType=1);
  `
      : `
        position: relative;
  `
    }
`
);

const MrasadatikBackground = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 90%;
  height: 90%;
  background: #0559fe;
  border-radius: 50%;
  z-index: -1;
`;

const MrasadatikLogoOverlay = styled(Box)`
  position: absolute;
  top: 90%;
  left: 90%;
  transform: translate(-90%, -90%);
`;

const MrasadatikOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgb(5, 213, 254);
  background: -moz-linear-gradient(
    180deg,
    rgba(5, 213, 254, 0) 60%,
    rgba(0, 0, 51, 0.5158263134355305) 77%,
    rgba(0, 0, 51, 0.7119047448080795) 85%,
    rgba(0, 0, 51, 1) 96%
  );
  background: -webkit-linear-gradient(
    180deg,
    rgba(5, 213, 254, 0) 60%,
    rgba(0, 0, 51, 0.5158263134355305) 77%,
    rgba(0, 0, 51, 0.7119047448080795) 85%,
    rgba(0, 0, 51, 1) 96%
  );
  background: linear-gradient(
    180deg,
    rgba(5, 213, 254, 0) 60%,
    rgba(0, 0, 51, 0.5158263134355305) 77%,
    rgba(0, 0, 51, 0.7119047448080795) 85%,
    rgba(0, 0, 51, 1) 96%
  );
  filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#05d5fe",endColorstr="#000033",GradientType=1);
`;

export default function Home() {
  const [sending, setSending] = React.useState(false);
  const coverLogoDivider = 3;
  const theme = useTheme();
  const [collapseMore, setCollapseMore] = React.useState<CollapseMoreProps>({
    vital: false,
    edu: false,
    skill: false,
    learning: false,
  });
  const requestCVDialogueState = useAppSelector(
    (state) => state.requestCVDialogueReducer.value
  );
  const dispatch = useAppDispatch();
  const handleRequestCVDialogueOpen = () => {
    dispatch(requestCVDialogueOpen());
    dispatch(socialDialogueClose());
  };

  const handleRequestCVDialogueClose = () => {
    dispatch(requestCVDialogueClose());
    dispatch(socialDialogueClose());
  };

  const requestFormLanding = useFormik({
    initialValues: {
      name: "",
      email: "",
      reason: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      setSending(true);
      // alert(JSON.stringify(values, null, 2));
      const abortLongFetch = new AbortController();
      const abortTimeoutId = setTimeout(() => abortLongFetch.abort(), 7000);
      fetch("/api/email/cv/", {
        signal: abortLongFetch.signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })
        .then((response) => {
          // response.status !== 200
          if (response.ok) {
            clearTimeout(abortTimeoutId);
            return response.json();
          }
          enqueueSnackbar("Network response was not OK!", {
            variant: "error",
          });
        })
        .then((data) => {
          if (data.emailSent) {
            setSending(false);
            enqueueSnackbar("Request sent successfully!", {
              variant: "success",
            });
            resetForm();
            dispatch(requestCVDialogueClose());
            dispatch(socialDialogueClose());
          } else {
            setSending(false);
            enqueueSnackbar("Error sending request!", { variant: "error" });
          }
        })
        .catch((error) => {
          setSending(false);
          enqueueSnackbar("Error sending request!", { variant: "error" });
        });
    },
  });

  return (
    <Container maxWidth={false}>
      <Box py={5}>
        <Box
          minHeight="100vh"
          component="section"
          id="landingCover"
          className="landingCover"
          py={5}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            container
            spacing={1}
            direction={{ xs: "column-reverse", md: "row" }}
          >
            <Grid
              item
              xs={12}
              md={6}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Box>
                <Box pb={3}>
                  <Typography variant="h5" component="span" display="block">
                    {`Md Asaduzzaman Atik! `}
                    <Tooltip title="Bangladeshi" followCursor>
                      <Link
                        href="https://wikipedia.org/wiki/Bangladeshis"
                        target="_blank"
                      >
                        <Image
                          src="/reserved/flags/bd.webp"
                          height={
                            parseFloat(theme.typography.h5.fontSize as string) *
                            theme.typography.fontSize
                          }
                          width={
                            ((parseFloat(
                              theme.typography.h5.fontSize as string
                            ) *
                              theme.typography.fontSize) /
                              3) *
                            4
                          }
                          alt="Bangladesh"
                        // sx={{ verticalAlign: "middle" }}
                        />
                      </Link>
                    </Tooltip>
                  </Typography>
                  <Typography
                    variant="caption"
                    component="span"
                    display="block"
                    color="text.disabled"
                    sx={{ fontFamily: "var(--font-charis_sil)" }}
                  >{`/muːˈhæməd ˌæsəˈduːdʒɑmən ˈætɪk/`}</Typography>
                </Box>
                <Box pb={2}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                    display="block"
                  >
                    {`A name of Arabic and Islamic origin, Md Asaduzzaman Atik
                    reflects kindness, courage, and a love for technology. 'Md,'
                    from 'Muhammad,' signifies respect. 'Asaduzzaman,' a blend
                    of 'Asad' (lion) and 'uzzaman' (age), portrays strength.
                    'Atik,' meaning 'antique,' adds wisdom. This friendly and
                    tech-savvy individual enjoys spreading joy and knowledge
                    with a touch of humor.`}
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    sx={{ textTransform: "none" }}
                    onClick={handleRequestCVDialogueOpen}
                  >
                    Request CV
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Box>
                <Tooltip title="mrasadatik" followCursor>
                  <Box sx={{ position: "relative", borderRadius: 200 }}>
                    <MrasadatikBackground />
                    <Image
                      src="/reserved/mrasadatik@14_7_2023.webp"
                      alt="mrasadatik"
                      width={1 * 300}
                      height={1 * 300}
                      sx={{
                        filter: "drop-shadow(5px 5px 5px #000033)",
                      }}
                    />
                    <MrasadatikOverlay />
                    <MrasadatikLogoOverlay>
                      <Link href="/">
                        <Image
                          src="/reserved/logo_and_favs/mrasadatik_logo@1000.png"
                          height={theme.typography.fontSize}
                          width={(theme.typography.fontSize / 11) * 40}
                          alt="mrasadatik_logo"
                          sx={{
                            filter: "drop-shadow(5px 5px 5px #000033)",
                          }}
                        />
                      </Link>
                    </MrasadatikLogoOverlay>
                  </Box>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box component="section" id="proSummary" className="proSummary">
          <Box py={1} pt={10}>
            <Box py={1} textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ verticalAlign: "middle" }}
              >
                Versatile Tech Guru{" "}
                <Link href="#proSummary">
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ p: 0, m: 0, verticalAlign: "middle" }}
                  />
                </Link>
              </Typography>
            </Box>
            <Grid
              container
              spacing={1}
              direction={{ xs: "column", md: "row" }}
            >

              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Box position="relative">
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/home/pro_summary.json"
                    sx={{ height: 300, width: 300, objectFit: "cover" }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box width="100%" position="relative">
                  <Typography variant="caption" color="text.disabled">
                    TL;DR
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary">
                    Atik is a highly skilled and passionate computer science and
                    engineering professional with a deep love for technology and
                    a natural curiosity for learning. He thrives on solving
                    challenges, enjoys bug fixing, and constantly seeks to
                    enhance user experiences through innovative solutions. With
                    a keen interest in smart homes, green energy, and
                    sustainable technologies, he envisions contributing to
                    eco-friendly advancements. Aspiring to make a meaningful
                    impact in the tech industry, Atik is enthusiastic about
                    sharing his knowledge with others.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box component="section" id="skillsSection" className="skillsSection">
          <Box py={1} pt={10}>
            <Box py={1} textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ verticalAlign: "middle" }}
              >
                Expert Acumen{" "}
                <Link href="#skillsSection">
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ p: 0, m: 0, verticalAlign: "middle" }}
                  />
                </Link>
              </Typography>
            </Box>
            <Grid container spacing={1} direction={{ xs: "column-reverse", md: "row" }}>

              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box width="100%" position="relative">
                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">Core Competencies</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Web Design and Development (Responsive & Cross-browser compatibility), Problem
                              Solving, Technical Support
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Programming Languages
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              JavaScript, TypeScript, PHP
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">Build Systems & Package Management</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Maven, NX, NPM, Yarn, PNPM
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Markup & Template Technologies
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              HTML, Markdown, Blade
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <Collapse in={collapseMore.skill} timeout="auto">
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Styling & UI Frameworks
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                CSS, SASS, SCSS, Bootstrap, Bulma, Semantic-UI, React Material-UI (MUI), React Bootstrap, Angular Material, ngx-bootstrap, Clarity Design, ... ...more on demand
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                JavaScript Libraries & Frameworks
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                jQuery, ReactJS, Next.js
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Runtime Environments & Web Frameworks
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Node.js, NestJS
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Database Management Systems
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                MySQL
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Version Control Systems
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Git
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Virtualization Platforms
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Oracle VM VirtualBox
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    </Collapse>
                  </Timeline>
                  <ExpandMoreOverlay overlay={!collapseMore.skill}>
                    <Box textAlign="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setCollapseMore((prev) => ({
                            ...prev,
                            skill: !prev.skill,
                          }))
                        }
                      >
                        {!collapseMore.skill ? `More ` : `Less `}
                        {collapseMore.skill ? (
                          <UnfoldLessIcon fontSize="small" />
                        ) : (
                          <UnfoldMoreIcon fontSize="small" />
                        )}
                      </Button>
                    </Box>
                  </ExpandMoreOverlay>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Box position="relative">
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/home/skills.json"
                    sx={{ height: 300, width: 300, objectFit: "cover" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box component="section" id="learningSection" className="learningSection">
          <Box py={1} pt={10}>
            <Box py={1} textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ verticalAlign: "middle" }}
              >
                Learning Endeavors{" "}
                <Link href="#learningSection">
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ p: 0, m: 0, verticalAlign: "middle" }}
                  />
                </Link>
              </Typography>
            </Box>
            <Grid container spacing={1} direction={{ xs: "column", md: "row" }}>

              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Box position="relative">
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/home/learning.json"
                    sx={{ height: 300, width: 300, objectFit: "cover" }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box width="100%" position="relative">
                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Ongoing Mastery</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Python, Django Rest Framework, Kotlin, Spring Boot, Express.js, PostgreSQL
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">On Hold Ventures
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              C, C++, Java, Django, Spring, MongoDB, Apache, NGINX, Gradle, Docker
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Future Horizons
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Kubernetes, Argo CD, Terraform, Ansible, Jenkins, Bazel, GitLab, GraphQL, Angular, Gatsby
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <Collapse in={collapseMore.learning} timeout="auto">
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Previous Ventures
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Pug, Twig, Laravel, Django Template, Adobe Audition, Adobe After Effects, Adobe Photoshop, Adobe Illustrator
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Foundational Insights
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Ethereum, Robotics, Solar Technologies, Blockchain, IoT, Building Server, Networking, Cloud Computing, AI/ML, Cybersecurity, DevOps, Quantum Computing, pfSense, and other evolving interests—continuously expanding my learning horizons
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    </Collapse>
                  </Timeline>
                  <ExpandMoreOverlay overlay={!collapseMore.learning}>
                    <Box textAlign="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setCollapseMore((prev) => ({
                            ...prev,
                            learning: !prev.learning,
                          }))
                        }
                      >
                        {!collapseMore.learning ? `More ` : `Less `}
                        {collapseMore.learning ? (
                          <UnfoldLessIcon fontSize="small" />
                        ) : (
                          <UnfoldMoreIcon fontSize="small" />
                        )}
                      </Button>
                    </Box>
                  </ExpandMoreOverlay>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box component="section" id="careerSection" className="careerSection">
          <Box py={1} pt={10}>
            <Box py={1} textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ verticalAlign: "middle" }}
              >
                Career Track Record{" "}
                <Link href="#careerSection">
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ p: 0, m: 0, verticalAlign: "middle" }}
                  />
                </Link>
              </Typography>
            </Box>
            <Grid container spacing={1} direction={{ xs: "column-reverse", md: "row" }}>

              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box width="100%" position="relative">
                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Founder
                            </Typography>
                            <Link href="https://www.argonedge.com">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                ArgonEdge
                              </Typography>
                            </Link>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              2024 - present
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Software Engineer Intern
                            </Typography>
                            <Link
                              target="_blank"
                              href="https://www.hrhouz.com?utm_source=www.asadatik.dev&utm_medium=showcase&utm_term=hr%2Bhiring%2Breference%2Bcheck%2Bresume%2Bscanner&utm_content=hrhouz.com%2Fimages%2Flogo%2Flogo.svg"
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                HRHouz
                              </Typography>
                            </Link>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              September 2024 - present
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                  </Timeline>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Box position="relative">
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/home/work.json"
                    sx={{ height: 300, width: 300, objectFit: "cover" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box component="section" id="aboutSection" className="aboutSection">
          <Box py={1} pt={10}>
            <Box py={1} textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ verticalAlign: "middle" }}
              >
                Vital Stats{" "}
                <Link href="#aboutSection">
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ p: 0, m: 0, verticalAlign: "middle" }}
                  />
                </Link>
              </Typography>
            </Box>
            <Grid container spacing={1} direction={{ xs: "column", md: "row" }}>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Box position="relative">
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/home/vital_stats.json"
                    sx={{ height: 300, width: 300, objectFit: "cover" }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box width="100%" position="relative">
                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">Born</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Bangladesh
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              21st century
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">Hobbies</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Coding
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Love to Talk & Explore
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Green & Sustainable Energy, Net Zero House, Home Automation, IoT and Smart Devices,
                              Machine Learning, AI, Technology, Coding, Space,
                              Universe, Multiverse
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <Collapse in={collapseMore.vital} timeout="auto">
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Languages Spoken
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Bengali, Hindi, English
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">Blood</Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                B+
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Marital Status
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Single and ready to binary
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">Religion</Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Islam
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    </Collapse>
                  </Timeline>
                  <ExpandMoreOverlay overlay={!collapseMore.vital}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setCollapseMore((prev) => ({
                          ...prev,
                          vital: !prev.vital,
                        }))
                      }
                    >
                      {!collapseMore.vital ? `More ` : `Less `}
                      {collapseMore.vital ? (
                        <UnfoldLessIcon fontSize="small" />
                      ) : (
                        <UnfoldMoreIcon fontSize="small" />
                      )}
                    </Button>
                  </ExpandMoreOverlay>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box
          component="section"
          id="academicSection"
          className="academicSection"
        >
          <Box py={1} pt={10}>
            <Box py={1} textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ verticalAlign: "middle" }}
              >
                Academic Journey{" "}
                <Link href="#academicSection">
                  <LinkIcon
                    fontSize="inherit"
                    sx={{ p: 0, m: 0, verticalAlign: "middle" }}
                  />
                </Link>
              </Typography>
            </Box>
            <Grid
              container
              spacing={1}
              direction={{ xs: "column-reverse", md: "row" }}
            >
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box width="100%" position="relative">
                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">B.Sc. in CSE</Typography>
                            <Typography variant="body2" color="text.secondary">
                              East West University
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              2023 - present (Anticipated Graduation: 2027)
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">Higher School</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Amtali Government College
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              2019 - 2021 (Graduated: 2021)
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Paper elevation={5}>
                          <Box p={2}>
                            <Typography variant="h6">
                              Secondary School
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Amtali Government A. K. Pilot High School
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              2017 - 2019 (Completed: 2019)
                            </Typography>
                          </Box>
                        </Paper>
                      </TimelineContent>
                    </TimelineItem>
                    <Collapse in={collapseMore.edu} timeout="auto">
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Junior School
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Amtali Government A. K. Pilot High School
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                2014 - 2016 (Completed: 2016)
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Primary School
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Amtali M. U. Girls High School attached
                                Government Primary School
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                2013 (Completed: 2013)
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Primary School
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Anchal International Ideal School, Amtali
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                2013
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Primary School
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Amtali Bandar Model Government Primary School
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                2011
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Paper elevation={5}>
                            <Box p={2}>
                              <Typography variant="h6">
                                Primary School
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Amtali Pre-Cadet School
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                2007 - 2010, 2012
                              </Typography>
                            </Box>
                          </Paper>
                        </TimelineContent>
                      </TimelineItem>
                    </Collapse>
                  </Timeline>
                  <ExpandMoreOverlay overlay={!collapseMore.edu}>
                    <Box textAlign="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setCollapseMore((prev) => ({
                            ...prev,
                            edu: !prev.edu,
                          }))
                        }
                      >
                        {!collapseMore.edu ? `More ` : `Less `}
                        {collapseMore.edu ? (
                          <UnfoldLessIcon fontSize="small" />
                        ) : (
                          <UnfoldMoreIcon fontSize="small" />
                        )}
                      </Button>
                    </Box>
                  </ExpandMoreOverlay>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
              >
                <Box position="relative">
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/home/education.json"
                    sx={{ height: 300, width: 300, objectFit: "cover" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Dialog
          maxWidth="xs"
          open={requestCVDialogueState}
          onClose={handleRequestCVDialogueClose}
          aria-labelledby="social-dialog"
        >
          <DialogTitle>Request CV</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="form"
              onSubmit={requestFormLanding.handleSubmit}
              pt={1}
            >
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Name*"
                type="text"
                value={requestFormLanding.values.name}
                onChange={requestFormLanding.handleChange}
                onBlur={requestFormLanding.handleBlur}
                error={
                  requestFormLanding.touched.name &&
                  Boolean(requestFormLanding.errors.name)
                }
                helperText=" "
              />
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email*"
                type="email"
                value={requestFormLanding.values.email}
                onChange={requestFormLanding.handleChange}
                onBlur={requestFormLanding.handleBlur}
                error={
                  requestFormLanding.touched.email &&
                  Boolean(requestFormLanding.errors.email)
                }
                helperText=" "
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                id="reason"
                name="reason"
                label="Reason*"
                type="text"
                value={requestFormLanding.values.reason}
                onChange={requestFormLanding.handleChange}
                onBlur={requestFormLanding.handleBlur}
                error={
                  requestFormLanding.touched.reason &&
                  Boolean(requestFormLanding.errors.reason)
                }
                helperText=" "
              />
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <LoadingButton
                  id="reasonSend"
                  name="reasonSend"
                  type="submit"
                  loading={sending}
                  variant="contained"
                  loadingPosition="start"
                  startIcon={<SendIcon />}
                >
                  {sending ? "Sending Request" : "Request"}
                </LoadingButton>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRequestCVDialogueClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
